/**
 * AOI clip — spec §G6 ("AOI clip & swipe (mutually exclusive)"), whose reference
 * implementation is copied below essentially verbatim — this is the code that shipped, and
 * the client's item 13 ("the clipped area does not stay where it is supposed to and does not
 * align with the AOI") is this snippet's bug, not a regression of it:
 *
 * ```ts
 * function updateAoiClip(map: L.Map, aoi: LatLng[]|null, on: boolean) {
 *   const panes = ['rasterPane','choroPane'].map(p => map.getPane(p)!);
 *   if (!on || !aoi) { panes.forEach(p => p.style.clipPath = ''); return; }
 *   const poly = aoi.map(ll => { const p = map.latLngToContainerPoint(ll); return `${p.x}px ${p.y}px`; }).join(',');
 *   panes.forEach(p => p.style.clipPath = `polygon(${poly})`);
 * }
 * map.on('move zoom viewreset', () => updateAoiClip(map, aoi, aoiClip));
 * ```
 *
 * **Why container pixels were wrong.** `rasterPane`/`choroPane` are children of Leaflet's
 * `.leaflet-map-pane`, which Leaflet pans by rewriting its own `transform: translate3d` —
 * the pane's children never move in the DOM, only that ancestor transform does. A CSS
 * `clip-path` on an element is resolved in *that element's own* coordinate space, which for
 * a pane child is **layer points** (origin at `map.getPixelOrigin()`), not container pixels.
 * So a mask built from `latLngToContainerPoint` was always off by exactly the pan distance —
 * right the instant it was drawn, drifting from the AOI on every subsequent pan, which is
 * exactly the client's complaint. `move` in the old subscription papered over this by
 * recomputing the (still-wrong) container polygon on every frame of a drag, which hid the
 * defect while dragging and re-exposed it the moment the pan stopped moving but the pane's
 * transform did not reset. `ScopeClip.ts`/`ProgrammeClip.ts` already use layer points and
 * `path()` for this reason (see their headers) — this module now reuses that same builder
 * (`ringsClipPath`) instead of carrying a second, wrong one.
 *
 * `rasterPane` and `choroPane` are the *thematic* panes only: §F5.10's clip masks the data,
 * never the basemap or the §F5.8 boundaries, so the reader can still see where the AOI sits.
 *
 * Ownership: this module and `Swipe.tsx` both write `clip-path` on the same two panes, and
 * §G6 makes them mutually exclusive in the store (`setAoiClip`/`setSwipe`). Each effect
 * early-returns when its own mode is off, so exactly one of them ever registers a cleanup
 * and the hand-off (React runs every destroy in a commit before any create) cannot leave a
 * stale mask behind.
 */
import { useEffect } from 'react';
import type L from 'leaflet';

import { useAppStore } from '../../lib/state';
import type { LatLon } from '../../lib/geo';
import { ringsClipPath, type LonLatRing } from './ProgrammeClip';

/** §G6: "`clip-path` on rasterPane+choroPane". */
export const CLIP_PANES = ['rasterPane', 'choroPane'] as const;

/** Write one `clip-path` value (or `''` to release) to both thematic panes. */
export function setPaneClip(map: L.Map, value: string): void {
  for (const name of CLIP_PANES) {
    const pane = map.getPane(name);
    if (pane) pane.style.clipPath = value;
  }
}

/**
 * Mask the thematic panes to the AOI ring, in **layer points** — see the module header for
 * why that (not container pixels) is the space a pane's own `clip-path` has to be built in.
 *
 * Builds on `ringsClipPath` (`ProgrammeClip.ts`, already reused by `ScopeClip.ts`) rather
 * than re-deriving the projection loop: `aoi` is §F1's `[lat, lon]` order, so it is flipped
 * to the `[lon, lat]` rings that helper takes, as its one ring.
 */
export function updateAoiClip(map: L.Map, aoi: readonly LatLon[] | null, on: boolean): void {
  if (!on || !aoi || aoi.length < 3) {
    setPaneClip(map, '');
    return;
  }
  const ring: LonLatRing = aoi.map(([lat, lon]) => [lon, lat]);
  setPaneClip(map, ringsClipPath(map, [ring]));
}

/**
 * Keep the §F1 `aoi`/`aoiClip` pair applied to the map, re-projecting on every view change.
 *
 * Called once, from `MapTools`. Nothing renders — the whole effect is two inline styles.
 */
export function useAoiClip(map: L.Map): void {
  const aoi = useAppStore((s) => s.aoi);
  const aoiClip = useAppStore((s) => s.aoiClip);

  useEffect(() => {
    if (!aoiClip || !aoi) return;
    const pts = aoi.pts;
    const apply = () => updateAoiClip(map, pts, true);
    apply();
    // §G6 named `move zoom viewreset` for the old container-pixel mask, which had to be
    // rebuilt on every pan frame. In layer points the mask is glued to the pane by
    // construction (a pan only moves `.leaflet-map-pane`'s transform, which carries the pane
    // — and this clip — with it), so `move` is dropped: recomputing it there would just
    // reproduce the same string the pane is already showing. `zoom`/`viewreset` stay because
    // they move the pixel origin layer points are measured from, and `resize` stays because
    // `invalidateSize` (MapView's ResizeObserver — collapsing the sidebar is 332 px) moves
    // that origin too, without firing any of the other three.
    map.on('zoom viewreset resize', apply);
    return () => {
      map.off('zoom viewreset resize', apply);
      setPaneClip(map, '');
    };
  }, [map, aoi, aoiClip]);
}
